import { useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Invite } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { formatNumber, joinedDate, plural } from "../../lib/format";
import { useUI } from "../../lib/uiStore";
import { CommunityIcon } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { Skeleton } from "../../components/ui/Skeleton";
import { Spinner } from "../../components/ui/Spinner";
import { toast } from "../../components/ui/Toast";
import { CommunityBadges } from "../../components/UserBadges";
import { IconCalendar } from "../../components/icons";
import { useAuth } from "../auth/AuthProvider";
import { CommunityBanner } from "./CommunityBanner";

/*
 * Community invites inside Atrium: the card under a chat message that links
 * one, and the popup that opens from it (or from the link itself) to join.
 */

export const useInvite = (code: string | null) =>
  useQuery({
    queryKey: ["invite", code],
    queryFn: () => api.get<Invite>(`/api/invites/${encodeURIComponent(code!)}`),
    enabled: !!code,
    retry: false,
    staleTime: 60_000,
  });

/** Accept (or reuse membership) and open the community. */
export function useEnterInvite() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async (invite: Invite) => {
    const r = invite.isMember
      ? { communityId: invite.community.id, channelId: invite.channelId }
      : await api.post<{ communityId: string; channelId: string | null }>(`/api/invites/${encodeURIComponent(invite.code)}/accept`);
    if (!invite.isMember) {
      await qc.invalidateQueries({ queryKey: ["my-communities"] });
      void qc.invalidateQueries({ queryKey: ["community"] });
      void qc.invalidateQueries({ queryKey: ["invite"] });
    }
    navigate(r.channelId ? `/c/${r.communityId}/${r.channelId}` : `/c/${r.communityId}`);
  };
}

function Counts({ invite, className }: { invite: Invite; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-3 tabular", className)}>
      <span className="inline-flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-success" aria-hidden />
        {formatNumber(invite.onlineCount)} online
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="size-2 rounded-full bg-fg-3" aria-hidden />
        {plural(invite.community.memberCount, "member")}
      </span>
    </div>
  );
}

/** Banner, icon, name, counts, age and description: the body of the popup and the /invite page. */
export function InviteDetails({ invite }: { invite: Invite }) {
  const c = invite.community;
  return (
    <div className="text-center">
      <CommunityBanner url={c.bannerUrl} preset={c.bannerColor} className="h-28 rounded-t-xl" />
      <div className="relative z-10 -mt-10 flex justify-center">
        <CommunityIcon src={c.iconUrl} name={c.name} size="xl" className="ring-4 ring-bg-subtle" />
      </div>
      <div className="px-6">
        {invite.inviter && <p className="mt-3 text-sm text-fg-3">{invite.inviter.displayName} invited you to join</p>}
        <h2 className="mt-1 flex items-center justify-center gap-1.5 text-xl font-semibold text-fg">
          <span className="truncate">{c.name}</span>
          <CommunityBadges badges={c.badges} />
        </h2>
        <Counts invite={invite} className="mt-1.5 justify-center" />
        {c.description && <p className="mt-3 line-clamp-4 whitespace-pre-line text-sm text-fg-2">{c.description}</p>}
        <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-fg-3">
          <IconCalendar className="icon size-3.5" aria-hidden />
          Created {joinedDate(invite.communityCreatedAt)}
        </p>
      </div>
    </div>
  );
}

/** "Join" for new members, "Go to community" for existing ones. */
export function InviteEnterButton({ invite, onEntered, className, size = "lg" }: { invite: Invite; onEntered?: () => void; className?: string; size?: "sm" | "md" | "lg" }) {
  const enter = useEnterInvite();
  const [pending, setPending] = useState(false);
  return (
    <Button
      variant={invite.isMember ? "secondary" : "primary"}
      size={size}
      className={className}
      loading={pending}
      onClick={async (e) => {
        e.stopPropagation();
        setPending(true);
        try {
          await enter(invite);
          onEntered?.();
        } catch (err) {
          toast.error(errorMessage(err, "Couldn't join this community"));
        } finally {
          setPending(false);
        }
      }}
    >
      {invite.isMember ? "Go to community" : "Join"}
    </Button>
  );
}

/** The invite popup, mounted once in the app shell and opened with useUI().openInvite(code). */
export function InviteModal() {
  const code = useUI((s) => s.inviteCode);
  const openInvite = useUI((s) => s.openInvite);
  const q = useInvite(code);
  const close = () => openInvite(null);
  return (
    <Dialog open={!!code} onOpenChange={(o) => !o && close()} title={q.data ? `Join ${q.data.community.name}` : "Community invite"} hideTitle size="sm" className="overflow-hidden [&>div:nth-child(2)]:p-0">
      {q.isPending ? (
        <div className="flex justify-center py-16 text-fg-3">
          <Spinner />
        </div>
      ) : q.isError ? (
        <div className="px-6 py-10 text-center">
          <h2 className="text-lg font-semibold">Invite unavailable</h2>
          <p className="mt-1 text-sm text-fg-3">{errorMessage(q.error, "This invite is invalid or has expired.")}</p>
          <Button className="mt-5" onClick={close}>
            Close
          </Button>
        </div>
      ) : (
        <>
          <InviteDetails invite={q.data} />
          <div className="p-6 pt-5">
            <InviteEnterButton invite={q.data} onEntered={close} className="w-full" />
          </div>
        </>
      )}
    </Dialog>
  );
}

/** The card under a message that links an invite. Opens the popup; members can go straight in. */
export function InviteEmbed({ code }: { code: string }) {
  const { me } = useAuth();
  const q = useInvite(me ? code : null);
  const openInvite = useUI((s) => s.openInvite);
  if (!me) return null;
  const shell = "mt-1.5 block w-full max-w-[432px] overflow-hidden rounded-lg border border-line bg-surface text-left";
  if (q.isPending)
    return (
      <div className={cn(shell, "p-3")} aria-busy>
        <Skeleton className="h-3 w-40" />
        <div className="mt-3 flex items-center gap-3">
          <Skeleton className="size-12 rounded-xl" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      </div>
    );
  if (q.isError)
    return (
      <div className={cn(shell, "p-3")}>
        <p className="text-xs font-semibold uppercase tracking-wide text-fg-3">Community invite</p>
        <p className="mt-1.5 text-sm text-fg-2">This invite is invalid or has expired.</p>
      </div>
    );
  const inv = q.data;
  const c = inv.community;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => {
        e.stopPropagation();
        openInvite(code);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openInvite(code);
        }
      }}
      className={cn(shell, "cursor-pointer transition-colors hover:border-line-strong")}
      aria-label={`Invite to ${c.name}`}
    >
      <CommunityBanner url={c.bannerUrl} preset={c.bannerColor} className="h-16" />
      <div className="p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-fg-3">{inv.inviter ? `${inv.inviter.displayName} invited you to join` : "You've been invited to join"}</p>
        <div className="mt-2 flex items-center gap-3">
          <CommunityIcon src={c.iconUrl} name={c.name} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-base font-semibold text-fg">{c.name}</span>
              <CommunityBadges badges={c.badges} />
            </div>
            <Counts invite={inv} className="text-xs" />
          </div>
          {inv.isMember ? (
            <InviteEnterButton invite={inv} size="sm" />
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                openInvite(code);
              }}
            >
              Join
            </Button>
          )}
        </div>
        {c.description && <p className="mt-2 line-clamp-2 text-sm text-fg-2">{c.description}</p>}
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-fg-3">
          <IconCalendar className="icon size-3.5" aria-hidden />
          Created {joinedDate(inv.communityCreatedAt)}
        </p>
      </div>
    </div>
  );
}
